import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { FiCalendar, FiSearch, FiUser } from "react-icons/fi";
import { useAppSelector } from "../../store/hooks";
import {
  canAccessSurgeryBookingAdmin,
  canWriteSurgeryBooking,
} from "../../utils/surgeryBookingAccess";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminPrimaryButton,
} from "../../components/admin/AdminPageShell";
import SurgerySlotPicker from "../../components/surgery/SurgerySlotPicker";
import {
  DaySlotDto,
  formatLongDay,
  formatSlotRange,
  startOfDay,
  toYmd,
} from "../../utils/surgeryBookingCalendar";
import {
  SURGERY_CLIENT_REGION,
  SURGERY_CLIENT_TIMEZONE,
  ukTodayDate,
} from "../../utils/surgeryTimezones";
import SurgeryRecordsPanel from "../../components/surgery/SurgeryRecordsPanel";
import api from "../../utils/api";
import { useToast } from "../../components/utilities/Toast";

type LabInfo = {
  user_id: number;
  token: string;
  user_name?: string | null;
  email?: string | null;
  organisation?: string | null;
  region: string;
  timezone: string;
  verifySurgeryBooking?: boolean;
};

const emptySlots = (): DaySlotDto[] =>
  Array.from({ length: 24 }, (_, hour) => ({
    hour,
    bookedCount: 0,
    capacity: 1,
    available: true,
    myBooking: false,
    isPast: false,
    bookings: [],
  }));

const SurgerySlotsAdmin = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { showToast } = useToast();

  const canAccess = canAccessSurgeryBookingAdmin(user?.role, employee?.role);
  const canWrite = canWriteSurgeryBooking(user?.role, employee?.role);

  const todayUk = useMemo(() => ukTodayDate(), []);
  const today = startOfDay(todayUk);

  const [capacity, setCapacity] = useState<1 | 2 | 3>(1);
  const [capacityDraft, setCapacityDraft] = useState<1 | 2 | 3>(1);
  const [savingCapacity, setSavingCapacity] = useState(false);

  const [viewMonth, setViewMonth] = useState(() => {
    const d = ukTodayDate();
    d.setDate(1);
    return d;
  });
  const [selectedDate, setSelectedDate] = useState(() => ukTodayDate());
  const [selectedHours, setSelectedHours] = useState<number[]>([]);
  const [slots, setSlots] = useState<DaySlotDto[]>(emptySlots);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [labTokenInput, setLabTokenInput] = useState("");
  const [lab, setLab] = useState<LabInfo | null>(null);
  const [labLoading, setLabLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookingNote, setBookingNote] = useState("");
  const [recordsRefreshKey, setRecordsRefreshKey] = useState(0);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get("/api/surgery-booking/settings");
      const c = Number(res.data?.data?.concurrent_capacity);
      const next = ([1, 2, 3].includes(c) ? c : 1) as 1 | 2 | 3;
      setCapacity(next);
      setCapacityDraft(next);
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || "Failed to load capacity settings",
        "error",
      );
    }
  }, [showToast]);

  const fetchSlots = useCallback(
    async (date: Date, token: string) => {
      if (!token) {
        setSlots(emptySlots());
        return;
      }
      setSlotsLoading(true);
      try {
        const res = await api.get("/api/surgery-booking/slots", {
          params: { date: toYmd(date), lab_token: token },
        });
        const data = res.data?.data;
        setSlots(Array.isArray(data?.slots) ? data.slots : emptySlots());
        if (data?.capacity) {
          const c = Number(data.capacity);
          if ([1, 2, 3].includes(c)) {
            setCapacity(c as 1 | 2 | 3);
            setCapacityDraft(c as 1 | 2 | 3);
          }
        }
      } catch (err: any) {
        showToast(
          err?.response?.data?.message || "Failed to load slots",
          "error",
        );
        setSlots(emptySlots());
      } finally {
        setSlotsLoading(false);
      }
    },
    [showToast],
  );

  const lookupLab = useCallback(async () => {
    const token = labTokenInput.trim().toUpperCase();
    if (!token) {
      showToast("Enter a lab token", "error");
      return;
    }
    setLabLoading(true);
    try {
      const res = await api.get(
        `/api/surgery-booking/lab/${encodeURIComponent(token)}`,
      );
      const data = res.data?.data as LabInfo;
      setLab(data);
      setLabTokenInput(data.token);
      setSelectedHours([]);
      await fetchSlots(selectedDate, data.token);
      showToast(`Loaded ${data.organisation || data.user_name || data.token}`, "success");
    } catch (err: any) {
      setLab(null);
      setSlots(emptySlots());
      setSelectedHours([]);
      showToast(
        err?.response?.data?.message || "Lab not found",
        "error",
      );
    } finally {
      setLabLoading(false);
    }
  }, [labTokenInput, selectedDate, fetchSlots, showToast]);

  useEffect(() => {
    if (!canAccess) return;
    fetchSettings();
  }, [canAccess, fetchSettings]);

  useEffect(() => {
    if (!canAccess || !lab?.token) return;
    fetchSlots(selectedDate, lab.token);
  }, [canAccess, selectedDate, lab?.token, fetchSlots]);

  if (!canAccess) {
    return <Navigate to="/dashboard/profile" replace />;
  }

  const goPrevMonth = () => {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goNextMonth = () => {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const selectDate = (date: Date) => {
    const day = startOfDay(date);
    if (day.getTime() < today.getTime()) return;
    setSelectedDate(day);
    setSelectedHours([]);
    if (
      day.getMonth() !== viewMonth.getMonth() ||
      day.getFullYear() !== viewMonth.getFullYear()
    ) {
      setViewMonth(new Date(day.getFullYear(), day.getMonth(), 1));
    }
  };

  const toggleHour = (hour: number) => {
    if (!canWrite || !lab) return;
    setSelectedHours((prev) =>
      prev.includes(hour) ? prev.filter((h) => h !== hour) : [...prev, hour],
    );
  };

  const clearLab = () => {
    setLab(null);
    setLabTokenInput("");
    setSelectedHours([]);
    setBookingNote("");
    setSlots(emptySlots());
  };

  const saveCapacity = async () => {
    if (!canWrite || savingCapacity) return;
    setSavingCapacity(true);
    try {
      const res = await api.put("/api/surgery-booking/settings", {
        concurrent_capacity: capacityDraft,
      });
      const c = Number(res.data?.data?.concurrent_capacity) as 1 | 2 | 3;
      setCapacity(c);
      setCapacityDraft(c);
      showToast(`Live capacity set to ${c}`, "success");
      if (lab?.token) await fetchSlots(selectedDate, lab.token);
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || "Failed to update capacity",
        "error",
      );
    } finally {
      setSavingCapacity(false);
    }
  };

  const bookForClient = async () => {
    if (!canWrite || booking || !lab) return;
    if (selectedHours.length === 0) {
      showToast("Select at least one open slot", "error");
      return;
    }

    setBooking(true);
    try {
      const res = await api.post("/api/surgery-booking/admin/book", {
        date: toYmd(selectedDate),
        hours: selectedHours,
        lab_token: lab.token,
        note: bookingNote.trim() || undefined,
      });
      const booked = res.data?.data?.booked as
        | Array<{ ist_label?: string }>
        | undefined;
      const istHint =
        booked?.map((b) => b.ist_label).filter(Boolean).join("; ") || "";
      showToast(
        `Booked ${selectedHours.length} slot(s) for ${lab.token}${istHint ? ` · ${istHint}` : ""}`,
        "success",
      );
      setSelectedHours([]);
      setBookingNote("");
      setRecordsRefreshKey((k) => k + 1);
      await fetchSlots(selectedDate, lab.token);
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || "Failed to book for client",
        "error",
      );
      await fetchSlots(selectedDate, lab.token);
    } finally {
      setBooking(false);
    }
  };

  const selectedDayBookings = slots.flatMap((s) =>
    (s.bookings || []).map((b) => ({
      ...b,
      hour: s.hour,
      slotIstLabel: s.ist_label || b.ist_label,
    })),
  );

  return (
    <>
      <Helmet>
        <title>Surgery Slots</title>
      </Helmet>
      <AdminPageShell>
        <AdminPageHeader
          title="Surgery Slots"
          subtitle={
            canWrite
              ? "Look up a lab, book in their local time, capacity enforced on exact IST windows"
              : "View live surgery slot bookings (read only)"
          }
        />

        <div className="rounded-2xl border border-[#E8E8E4] bg-white p-5 mb-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-[#4A4A48]">
                Concurrent live surgeries
              </p>
              <p className="text-xs text-[#8A8A85] mt-1 max-w-xl">
                How many surgeries can run in the same overlapping IST
                timeframe. Capacity is checked on exact IST intervals (including
                :30 offsets from UK time).
              </p>
              <p className="text-xs text-[#8A8A85] mt-1">
                Current capacity:{" "}
                <strong className="text-[#4A4A48]">{capacity}</strong>
                <span className="text-[#C0C0BA]"> · </span>
                Lab clock:{" "}
                <strong className="text-[#4A4A48]">
                  {SURGERY_CLIENT_REGION} ({SURGERY_CLIENT_TIMEZONE})
                </strong>
              </p>
            </div>
            {canWrite ? (
              <div className="flex items-center gap-2 flex-wrap">
                {([1, 2, 3] as const).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setCapacityDraft(n)}
                    className={[
                      "h-10 w-10 rounded-xl text-sm font-semibold border transition",
                      capacityDraft === n
                        ? "bg-[#2fa9bf] border-[#2fa9bf] text-white"
                        : "bg-white border-[#DDDDD8] text-[#4A4A48] hover:bg-[#F8F8F6]",
                    ].join(" ")}
                  >
                    {n}
                  </button>
                ))}
                <AdminPrimaryButton
                  onClick={saveCapacity}
                  disabled={savingCapacity || capacityDraft === capacity}
                >
                  {savingCapacity ? "Saving…" : "Save"}
                </AdminPrimaryButton>
              </div>
            ) : null}
          </div>
        </div>

        <div className="rounded-2xl border border-[#E8E8E4] bg-white overflow-hidden mb-5">
          <div className="px-5 py-4 border-b border-[#F0F0EC] space-y-3">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F4F4F2] text-[#4A4A48]">
                <FiUser className="h-4 w-4" />
              </span>
              <div>
                <p className="text-sm font-semibold text-[#4A4A48]">
                  1. Look up lab
                </p>
                <p className="text-xs text-[#8A8A85]">
                  Enter lab token first — slots load in that lab&apos;s local
                  time ({SURGERY_CLIENT_REGION})
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <input
                type="text"
                value={labTokenInput}
                onChange={(e) => setLabTokenInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void lookupLab();
                  }
                }}
                placeholder="Lab token e.g. AB"
                className="w-full sm:max-w-xs rounded-xl border border-[#DDDDD8] bg-white px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 uppercase"
              />
              <AdminPrimaryButton
                onClick={() => void lookupLab()}
                disabled={labLoading || !labTokenInput.trim()}
              >
                {labLoading ? (
                  "Looking up…"
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    <FiSearch className="h-3.5 w-3.5" />
                    Look up
                  </span>
                )}
              </AdminPrimaryButton>
              {lab ? (
                <button
                  type="button"
                  onClick={clearLab}
                  className="text-xs font-medium text-[#8A8A85] hover:text-[#4A4A48] px-2 py-2"
                >
                  Clear
                </button>
              ) : null}
            </div>
            {lab ? (
              <div className="rounded-xl border border-[#E8E8E4] bg-[#FAFAF8] px-3 py-2.5 text-xs text-[#4A4A48] flex flex-wrap gap-x-4 gap-y-1">
                <span>
                  <strong>{lab.token}</strong>
                  {lab.organisation ? ` · ${lab.organisation}` : ""}
                </span>
                {lab.user_name ? <span>{lab.user_name}</span> : null}
                {lab.email ? (
                  <span className="text-[#8A8A85]">{lab.email}</span>
                ) : null}
                <span className="text-[#8A8A85]">
                  {lab.region} · {lab.timezone}
                </span>
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#F0F0EC]">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F4F4F2] text-[#4A4A48]">
                <FiCalendar className="h-4 w-4" />
              </span>
              <div>
                <p className="text-sm font-semibold text-[#4A4A48]">
                  2. Slot calendar
                </p>
                <p className="text-xs text-[#8A8A85]">
                  {lab
                    ? `${formatLongDay(selectedDate)} · ${SURGERY_CLIENT_REGION} local · capacity ${capacity}`
                    : "Enter a lab token above to load slots"}
                </p>
              </div>
            </div>
          </div>

          {lab ? (
            <>
              <SurgerySlotPicker
                viewMonth={viewMonth}
                selectedDate={selectedDate}
                today={today}
                slots={slots}
                selectedHours={canWrite ? selectedHours : []}
                loading={slotsLoading}
                onPrevMonth={goPrevMonth}
                onNextMonth={goNextMonth}
                onSelectDate={selectDate}
                onToggleHour={toggleHour}
                showBookedMeta
                showRangeLabels
                readOnly={!canWrite}
              />

              {canWrite ? (
                <div className="border-t border-[#F0F0EC] px-5 py-4 bg-[#FAFAF8] flex flex-col gap-3">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-[#8A8A85] mb-1.5">
                      Note for surgery{" "}
                      <span className="font-normal normal-case tracking-normal">
                        (optional — not visible to client)
                      </span>
                    </label>
                    <textarea
                      value={bookingNote}
                      onChange={(e) => setBookingNote(e.target.value)}
                      rows={2}
                      maxLength={5000}
                      placeholder="Internal note for this booking…"
                      className="w-full rounded-xl border border-[#DDDDD8] bg-white px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 resize-y min-h-[64px]"
                    />
                  </div>
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <p className="text-xs text-[#8A8A85] min-w-0">
                      {selectedHours.length === 0
                        ? "Select open UK local slots — each shows the exact IST window underneath"
                        : `${selectedHours.length} slot(s): ${selectedHours
                            .slice()
                            .sort((a, b) => a - b)
                            .map((h) => {
                              const slot = slots.find((s) => s.hour === h);
                              return `${formatSlotRange(h)}${slot?.ist_range_24 ? ` → IST ${slot.ist_range_24}` : ""}`;
                            })
                            .join("; ")}`}
                    </p>
                    <AdminPrimaryButton
                      onClick={bookForClient}
                      disabled={booking || selectedHours.length === 0}
                    >
                      {booking ? "Booking…" : "Book for client"}
                    </AdminPrimaryButton>
                  </div>
                </div>
              ) : null}

              {selectedDayBookings.length > 0 ? (
                <div className="border-t border-[#F0F0EC] px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8A85] mb-2">
                    Bookings on {formatLongDay(selectedDate)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedDayBookings.map((b) => (
                      <span
                        key={`${b.id}-${b.hour}`}
                        className="inline-flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 rounded-xl border border-[#E8E8E4] bg-[#F8F8F6] px-3 py-1.5 text-xs text-[#4A4A48]"
                      >
                        <span>
                          <strong>
                            {formatSlotRange(b.hour)} {SURGERY_CLIENT_REGION}
                          </strong>
                          <span className="text-[#8A8A85]">
                            {" "}
                            · {b.lab_token}
                          </span>
                        </span>
                        <span className="text-[#8A8A85]">
                          {b.ist_label || b.slotIstLabel || "—"}
                        </span>
                        <span className="text-[#C0C0BA] hidden sm:inline">·</span>
                        <span className="text-[#8A8A85]">{b.booked_by}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="px-5 py-12 text-center text-sm text-[#8A8A85]">
              Look up a lab token to view and book surgery slots.
            </div>
          )}
        </div>

        <SurgeryRecordsPanel
          canWrite={canWrite}
          refreshKey={recordsRefreshKey}
        />
      </AdminPageShell>
    </>
  );
};

export default SurgerySlotsAdmin;
